import { useMemo } from 'react';
import { Customer } from '@/lib/types';
import { normalizeCity, normalizeSuburb, normalizeProvinceState } from '@/lib/addressUtils';
import { saAddressService } from '@/lib/southAfricanAddressService';

export type AddressHierarchy = {
  [provinceState: string]: {
    [city: string]: {
      suburbs: string[];
      postalCodes: string[];
    };
  };
};

export type AddressRelationships = {
  suburbToCity: Record<string, string>;
  cityToProvince: Record<string, string>;
  suburbToProvince: Record<string, string>;
  postalCodeToCity: Record<string, string>;
};

/**
 * Hook to build and manage address hierarchy from customer data
 */
export function useAddressHierarchy(customers: Customer[]) {
  const { hierarchy, relationships } = useMemo(() => {
    const addressHierarchy: AddressHierarchy = {};
    const suburbToCity: Record<string, string> = {};
    const cityToProvince: Record<string, string> = {};
    const suburbToProvince: Record<string, string> = {};
    const postalCodeToCity: Record<string, string> = {};

    // Initialize with South African static data first
    saAddressService.getAllProvinces().forEach(province => {
      addressHierarchy[province] = {};
      
      saAddressService.getCitiesForProvince(province).forEach(city => {
        addressHierarchy[province][city] = {
          suburbs: saAddressService.getSuburbsForCity(city),
          postalCodes: saAddressService.getPostalCodesForCity(city),
        };
        
        // Build SA relationships
        cityToProvince[city] = province;
        
        saAddressService.getSuburbsForCity(city).forEach(suburb => {
          suburbToCity[suburb] = city;
          suburbToProvince[suburb] = province;
        });
        
        saAddressService.getPostalCodesForCity(city).forEach(postalCode => {
          postalCodeToCity[postalCode] = city;
        });
      });
    });

    // Then merge customer data (customer data takes priority for conflicts)
    customers.forEach(customer => {
      const normalizedCity = normalizeCity(customer.city);
      const normalizedSuburb = normalizeSuburb(customer.suburb);
      const normalizedProvince = normalizeProvinceState(customer.province_state);
      const normalizedPostalCode = customer.postal_code?.trim();

      // Skip if essential data is missing
      if (!normalizedCity && !normalizedSuburb && !normalizedProvince) return;

      // Build province level
      if (normalizedProvince) {
        if (!addressHierarchy[normalizedProvince]) {
          addressHierarchy[normalizedProvince] = {};
        }

        // Build city level within province
        if (normalizedCity) {
          if (!addressHierarchy[normalizedProvince][normalizedCity]) {
            addressHierarchy[normalizedProvince][normalizedCity] = {
              suburbs: [],
              postalCodes: [],
            };
          }

          // Build relationships
          cityToProvince[normalizedCity] = normalizedProvince;
          
          if (normalizedPostalCode && !addressHierarchy[normalizedProvince][normalizedCity].postalCodes.includes(normalizedPostalCode)) {
            addressHierarchy[normalizedProvince][normalizedCity].postalCodes.push(normalizedPostalCode);
            postalCodeToCity[normalizedPostalCode] = normalizedCity;
          }

          // Build suburb level within city
          if (normalizedSuburb && !addressHierarchy[normalizedProvince][normalizedCity].suburbs.includes(normalizedSuburb)) {
            addressHierarchy[normalizedProvince][normalizedCity].suburbs.push(normalizedSuburb);
            suburbToCity[normalizedSuburb] = normalizedCity;
            suburbToProvince[normalizedSuburb] = normalizedProvince;
          }
        }
      }
    });

    // Sort all arrays for consistent display
    Object.values(addressHierarchy).forEach(cities => {
      Object.values(cities).forEach(cityData => {
        cityData.suburbs.sort();
        cityData.postalCodes.sort();
      });
    });

    return {
      hierarchy: addressHierarchy,
      relationships: {
        suburbToCity,
        cityToProvince,
        suburbToProvince,
        postalCodeToCity,
      }
    };
  }, [customers]);

  // Utility functions with SA address service fallback
  const getSuburbsForCity = (city: string): string[] => {
    const normalizedCity = normalizeCity(city);
    const province = relationships.cityToProvince[normalizedCity];
    if (province && hierarchy[province]?.[normalizedCity]) {
      return hierarchy[province][normalizedCity].suburbs;
    }
    // Fallback to SA address service
    return saAddressService.getSuburbsForCity(city);
  };

  const getCityForSuburb = (suburb: string): string | undefined => {
    const normalizedSuburb = normalizeSuburb(suburb);
    const city = relationships.suburbToCity[normalizedSuburb];
    if (city) return city;
    // Fallback to SA address service
    return saAddressService.getCityForSuburb(suburb);
  };

  const getProvinceForCity = (city: string): string | undefined => {
    const normalizedCity = normalizeCity(city);
    const province = relationships.cityToProvince[normalizedCity];
    if (province) return province;
    // Fallback to SA address service
    return saAddressService.getProvinceForCity(city);
  };

  const getProvinceForSuburb = (suburb: string): string | undefined => {
    const normalizedSuburb = normalizeSuburb(suburb);
    const province = relationships.suburbToProvince[normalizedSuburb];
    if (province) return province;
    // Fallback to SA address service
    return saAddressService.getProvinceForSuburb(suburb);
  };

  const getCitiesForProvince = (province: string): string[] => {
    const normalizedProvince = normalizeProvinceState(province);
    const cities = Object.keys(hierarchy[normalizedProvince] || {});
    if (cities.length > 0) return cities.sort();
    // Fallback to SA address service
    return saAddressService.getCitiesForProvince(province);
  };

  const getPostalCodesForCity = (city: string): string[] => {
    const normalizedCity = normalizeCity(city);
    const province = relationships.cityToProvince[normalizedCity];
    if (province && hierarchy[province]?.[normalizedCity]) {
      return hierarchy[province][normalizedCity].postalCodes;
    }
    // Fallback to SA address service
    return saAddressService.getPostalCodesForCity(city);
  };

  const validatePostalCode = (postalCode: string, province?: string): boolean => {
    if (!province) return true;
    return saAddressService.validatePostalCodeForProvince(postalCode, province);
  };

  const getSuggestions = (input: string, type: 'province' | 'city' | 'suburb') => {
    return saAddressService.getSuggestions(input, type);
  };

  const getAutoCompletionSuggestions = (field: 'suburb' | 'city' | 'province', value: string) => {
    return saAddressService.getAutoCompletionSuggestions(field, value);
  };

  const formatSuburbWithCity = (suburb: string): string => {
    const city = getCityForSuburb(suburb);
    return city ? `${suburb} (${city})` : suburb;
  };

  const formatCityWithProvince = (city: string): string => {
    const province = getProvinceForCity(city);
    return province ? `${city} (${province})` : city;
  };

  return {
    hierarchy,
    relationships,
    getSuburbsForCity,
    getCityForSuburb,
    getProvinceForCity,
    getProvinceForSuburb,
    getCitiesForProvince,
    getPostalCodesForCity,
    formatSuburbWithCity,
    formatCityWithProvince,
    validatePostalCode,
    getSuggestions,
    getAutoCompletionSuggestions,
  };
}